// src/utils/calculatorUtils.js

/**
 * Calcula la edad en años a partir de la fecha de nacimiento.
 * @param {string|Date} fechaNacimiento
 * @returns {number|string}
 */
export const calcularEdad = (fechaNacimiento) => {
  if (!fechaNacimiento) return "";
  const hoy = new Date();
  const nacimiento = new Date(fechaNacimiento);
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  const m = hoy.getMonth() - nacimiento.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) {
    edad--;
  }
  return edad;
};

/**
 * Calcula la Tasa de Filtración Glomerular (CKD-EPI legacy 2009).
 * Mantenido para retrocompatibilidad con Calculator.js.
 */
export const calcularTFG = ({ creatinina, unidad, paciente, raza }) => {
  if (!paciente || !creatinina) return null;

  const edad = calcularEdad(paciente.fechaNacimiento);
  const sexo = paciente.sexo ? paciente.sexo.toLowerCase() : "";
  let SCr = parseFloat(creatinina);

  if (isNaN(SCr) || SCr <= 0) return null;

  if (unidad === "μmol/L") SCr = SCr / 88.4;

  const kappa = sexo === "f" ? 0.7 : 0.9;
  const alpha = sexo === "f" ? -0.329 : -0.411;
  const multiplicadorSexo = sexo === "f" ? 1.018 : 1;
  const multiplicadorRaza = raza === "Negra" ? 1.159 : 1;

  const minSCr = Math.min(SCr / kappa, 1);
  const maxSCr = Math.max(SCr / kappa, 1);

  const TFG =
    141 *
    Math.pow(minSCr, alpha) *
    Math.pow(maxSCr, -1.209) *
    Math.pow(0.993, edad) *
    multiplicadorSexo *
    multiplicadorRaza;

  return TFG.toFixed(2);
};

/**
 * Calcula la Tensión Arterial Media (TAM) en mmHg.
 * Fórmula: TAM = (TAS + 2*TAD) / 3
 * 
 * @param {number|string} tas - Tensión Arterial Sistólica (mmHg)
 * @param {number|string} tad - Tensión Arterial Diastólica (mmHg)
 * @returns {{ valor: number, estado: string, color: string, badgeBg: string } | null}
 */
export const calcularTAM = (tas, tad) => {
  const s = parseFloat(tas);
  const d = parseFloat(tad);
  if (isNaN(s) || isNaN(d) || s <= 0 || d <= 0 || s <= d) return null;

  const tam = (s + 2 * d) / 3;
  const valor = Math.round(tam * 100) / 100;

  let estado = "Normal";
  let color = "#16a34a"; // verde
  let badgeBg = "#dcfce7";

  if (valor < 65) {
    estado = "Alerta (<65)";
    color = "#dc2626"; // rojo
    badgeBg = "#fee2e2";
  } else if (valor > 100) {
    estado = "Elevada (>100)";
    color = "#ea580c"; // naranja
    badgeBg = "#ffedd5";
  }

  return { valor, estado, color, badgeBg };
};

/**
 * Calcula la Tasa de Filtración Glomerular usando CKD-EPI 2021 (sin raza).
 * 
 * @param {number|string} scr - Creatinina sérica en mg/dL
 * @param {number|string} age - Edad en años
 * @param {string} sex - 'F' (femenino) o 'M' (masculino)
 * @returns {{ valor: number, estadio: string, descripcion: string, color: string, badgeBg: string } | null}
 */
export const calcularCKDEPI2021 = (scr, age, sex) => {
  const c = parseFloat(scr);
  const a = parseInt(age, 10);
  if (isNaN(c) || isNaN(a) || c <= 0 || a <= 0 || !sex) return null;

  const s = String(sex).trim().toUpperCase();
  const isFemale = s === "F";
  const kappa = isFemale ? 0.7 : 0.9;
  const alpha = isFemale ? -0.329 : -0.411;
  const factor = isFemale ? 1.012 : 1.0;

  const ratio = c / kappa;
  const minVal = Math.min(ratio, 1.0);
  const maxVal = Math.max(ratio, 1.0);

  const egfr = 142.0 * Math.pow(minVal, alpha) * Math.pow(maxVal, -1.2) * Math.pow(0.9938, a) * factor;
  const valor = Math.round(egfr);

  // Clasificación clínica KDIGO:
  let estadio = "G1";
  let descripcion = "Normal";
  let color = "#16a34a";
  let badgeBg = "#dcfce7";

  if (valor >= 90) {
    estadio = "G1";
    descripcion = "Normal (≥90)";
    color = "#16a34a";
    badgeBg = "#dcfce7";
  } else if (valor >= 60) {
    estadio = "G2";
    descripcion = "Leve (60-89)";
    color = "#2563eb";
    badgeBg = "#dbeafe";
  } else if (valor >= 45) {
    estadio = "G3a";
    descripcion = "Leve-Mod (45-59)";
    color = "#d97706";
    badgeBg = "#fef3c7";
  } else if (valor >= 30) {
    estadio = "G3b";
    descripcion = "Mod-Grave (30-44)";
    color = "#ea580c";
    badgeBg = "#ffedd5";
  } else if (valor >= 15) {
    estadio = "G4";
    descripcion = "Grave (15-29)";
    color = "#dc2626";
    badgeBg = "#fee2e2";
  } else {
    estadio = "G5";
    descripcion = "Falla renal (<15)";
    color = "#991b1b";
    badgeBg = "#fecaca";
  }

  return { valor, estadio, descripcion, color, badgeBg };
};

/**
 * Calcula el Índice de Masa Corporal (IMC) en kg/m².
 * El IMC estándar para adultos se aplica a partir de los 20 años (mayores de 20 años).
 * 
 * @param {number|string} pesoKg - Peso en kilogramos
 * @param {number|string} talla - Talla en metros o centímetros
 * @param {number|string} [edad] - Edad en años (opcional). Si se suministra y es menor de 20 años, retorna null.
 * @returns {{ valor: number, estado: string, color: string, badgeBg: string } | null}
 */
export const calcularIMC = (pesoKg, talla, edad = null) => {
  if (edad !== null && edad !== undefined && edad !== "") {
    const e = parseFloat(edad);
    if (!isNaN(e) && e < 20) return null;
  }

  const p = parseFloat(pesoKg);
  let t = parseFloat(talla);
  if (isNaN(p) || isNaN(t) || p <= 0 || t <= 0) return null;

  if (t > 3) {
    t = t / 100;
  }

  const imc = p / (t * t);
  const valor = Math.round(imc * 10) / 10;

  let estado = "Normal";
  let color = "#16a34a";
  let badgeBg = "#dcfce7";

  if (valor < 18.5) {
    estado = "Bajo peso";
    color = "#2563eb";
    badgeBg = "#dbeafe";
  } else if (valor <= 24.9) {
    estado = "Normal";
    color = "#16a34a";
    badgeBg = "#dcfce7";
  } else if (valor <= 29.9) {
    estado = "Sobrepeso";
    color = "#d97706";
    badgeBg = "#fef3c7";
  } else {
    estado = "Obesidad";
    color = "#dc2626";
    badgeBg = "#fee2e2";
  }

  return { valor, estado, color, badgeBg };
};
